// src/admin/plans/PlanFeaturesGrid.jsx — families × features, read-only (v223). Columns = the catalogue in the
// grid's own order (identical in every row); names from admin:plans.features.* (plansModel.featureLabelKey), else
// the cell's display_name (raw key, for a feature added later). `granted` is already resolved by the backend.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { featureLabelKey, gridRows } from './plansModel.js';
import { familyLabel } from './PlansTable.jsx';
import { COLORS, FONT_SANS, SEMANTIC } from '../../constants/theme.js';

const th = {
  textAlign: 'center', padding: '8px 10px', borderBottom: `2px solid ${COLORS.border}`, color: COLORS.textSecondary,
  fontWeight: 600, fontSize: '11px',
};
const td = { textAlign: 'center', padding: '8px 10px', borderBottom: `1px solid ${COLORS.border}`, fontSize: '13px' };

export default function PlanFeaturesGrid({ grid, familyOrder }) {
  const { t } = useTranslation('admin');
  const rows = gridRows(grid, familyOrder);
  if (!rows.length) return null;
  const catalogue = Array.isArray(rows[0].features) ? rows[0].features : [];
  const name = (c) => {
    const key = featureLabelKey(c);
    const fallback = c.display_name || c.feature_id;
    return key ? t(key, { defaultValue: fallback }) : fallback;
  };
  return (
    <section style={{ marginTop: '32px' }}>
      <h2 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 4px', color: COLORS.ink }}>{t('plans.grid.title')}</h2>
      <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: '0 0 12px' }}>{t('plans.grid.subtitle')}</p>
      <div style={{ overflowX: 'auto' }}>
        <table aria-label={t('plans.grid.title')} style={{ borderCollapse: 'collapse', fontFamily: FONT_SANS, width: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left' }}>{t('plans.grid.family')}</th>
              {catalogue.map((c) => <th key={c.feature_id} style={th}>{name(c)}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const notes = [
                r.wildcard ? t('plans.grid.wildcard') : null,
                Array.isArray(r.plan_ids) && r.plan_ids.length === 0 ? t('plans.grid.noPlans') : null,
              ].filter(Boolean);
              return (
                <tr key={r.grant_key}>
                  <td style={{ ...td, textAlign: 'left', color: COLORS.ink, fontWeight: 600 }}>
                    {familyLabel(t, r.grant_key)}
                    {notes.length > 0 && <span style={{ color: COLORS.textSecondary, fontWeight: 400 }}> — {notes.join(' · ')}</span>}
                  </td>
                  {catalogue.map((c) => {
                    const granted = !!(r.features ?? []).find((x) => x.feature_id === c.feature_id)?.granted;
                    return (
                      <td key={c.feature_id} style={td}>
                        <span
                          aria-label={granted ? t('plans.grid.granted') : t('plans.grid.notGranted')}
                          style={{ color: granted ? SEMANTIC.verde.fg : COLORS.textTertiary }}
                        >
                          {granted ? '✓' : '—'}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
